import { AppBar, Box, Container, IconButton, Toolbar, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AppButton } from '@/shared/components/buttons';
import { CreateAlertFormValues } from '../types';
import { AlertLocationSection } from './AlertLocationSection';
import { AlertProductSection } from './AlertProductSection';

export interface AlertFormProps {
  defaultValues: CreateAlertFormValues;
  onSubmit: (data: CreateAlertFormValues) => Promise<void>;
  title: string;
  submitLabel: string;
  submittingLabel: string;
  testIdPrefix: string;
}

export function AlertForm({
  defaultValues,
  onSubmit,
  title,
  submitLabel,
  submittingLabel,
  testIdPrefix,
}: AlertFormProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm<CreateAlertFormValues>({
    defaultValues,
    mode: 'onChange',
  });

  const handleFormSubmit = async (data: CreateAlertFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{ bgcolor: 'background.default', minHeight: '100vh' }}
      data-testid={`${testIdPrefix}-page`}
    >
      {/* Header */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'primary.main',
          borderRadius: '0 0 16px 16px',
          zIndex: theme => theme.zIndex.appBar,
          paddingTop: 'env(safe-area-inset-top)',
        }}
        data-testid={`${testIdPrefix}-header`}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            aria-label={t('common.back')}
            data-testid={`${testIdPrefix}-back-button`}
          >
            <ArrowBack />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center',
              mr: 5,
            }}
          >
            <Typography variant="h6" component="div" sx={{ color: 'white', fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Form */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box
          component="form"
          onSubmit={handleSubmit(handleFormSubmit)}
          data-testid={`${testIdPrefix}-form-area`}
        >
          <AlertLocationSection
            control={control}
            setValue={setValue}
            errors={errors}
            getValues={getValues}
            trigger={trigger}
          />
          <AlertProductSection
            control={control}
            setValue={setValue}
            errors={errors}
            getValues={getValues}
            trigger={trigger}
            watch={watch}
          />

          <AppButton
            type="submit"
            variant="primary"
            fullWidth
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
            data-testid={`${testIdPrefix}-submit-button`}
          >
            {isSubmitting ? submittingLabel : submitLabel}
          </AppButton>
        </Box>
      </Container>
    </Box>
  );
}
